/**
 * Answer-engine optimisation layer for the blog.
 *
 * Each entry gives a post an extractable, self-contained answer (the "short
 * answer" box rendered at the top of the article) plus a handful of question
 * and answer pairs that are rendered on the page AND emitted as FAQPage
 * structured data. Answer engines quote short, complete, standalone sentences;
 * every answer below is written so it still makes sense with no surrounding
 * context.
 */
export interface PostAnswers {
  /** 40-60 words. Must stand alone as a complete answer. */
  tldr: string;
  faqs: { q: string; a: string }[];
}

export const POST_ANSWERS: Record<string, PostAnswers> = {
  "one-thing-a-day-business-progress": {
    tldr: "The one-thing-a-day method means naming a single specific action each morning that moves a business forward, recording what actually happened that evening, and reading the pattern at month end. It works because one recorded outcome is real evidence, while a ten-item task list only measures intention.",
    faqs: [
      {
        q: "What is the one-thing-a-day method?",
        a: "The one-thing-a-day method is a daily loop with three parts: name one specific move each morning, record the real outcome each evening, and review the pattern monthly. It replaces long task lists with a single recorded action per business per day.",
      },
      {
        q: "Why track one task a day instead of a full to-do list?",
        a: "A single daily move forces you to rank what matters. Founders with ten-item lists tend to finish the two easiest items and defer the one that would have mattered, which hides the deferral until the end of a quarter.",
      },
      {
        q: "How do you track daily progress across several businesses?",
        a: "Keep one separate record per business so each has its own daily move, outcome, and history. Shared lists blur which venture is actually moving; separate records let each business be judged on its own motion.",
      },
    ],
  },
  "monthly-bold-move-framework": {
    tldr: "A monthly bold move is one deliberately uncomfortable action per business per month — raising a price, cutting a product, pitching a partner — that is chosen on the first of the month, written down, and reviewed at month end. It is sized so failure is survivable but the outcome is genuinely uncertain.",
    faqs: [
      {
        q: "What is a monthly bold move?",
        a: "A monthly bold move is a single deliberately uncomfortable business action committed to at the start of each month. It is bigger than a daily task, uncertain in outcome, and reviewed honestly at month end.",
      },
      {
        q: "How do you choose a bold move for the month?",
        a: "Pick the action you have been quietly avoiding that would change something real if it worked: a price increase, a hard conversation, a launch date, a cut. If the outcome is certain, it is not a bold move.",
      },
      {
        q: "What if the bold move fails?",
        a: "A failed bold move is still a result. Size each one so failure is survivable, record what happened, and use the record to see which category of risk your business actually rewards.",
      },
    ],
  },
  "running-multiple-businesses-without-losing-the-thread": {
    tldr: "To run multiple businesses without losing the thread, keep completely separate records for each one — separate daily moves, journals, contacts and history — and review them side by side on a fixed cadence. Blended task lists hide which venture is stalling until the numbers force the issue.",
    faqs: [
      {
        q: "How do you manage several businesses at once?",
        a: "Give each business its own record of daily moves, notes and contacts, then review all of them on the same weekly or monthly cadence. Separation makes it obvious which business is moving and which is only receiving attention.",
      },
      {
        q: "Should you use one app for all your businesses?",
        a: "One app is fine as long as it isolates data per business. The failure mode is a single shared list, because a busy week on one venture reads as progress across all of them.",
      },
      {
        q: "How much time does each business need per day?",
        a: "Under a minute of recording per business, plus the work itself. The record is not the work — it is the evidence that the work happened.",
      },
    ],
  },
  "why-streak-based-apps-backfire": {
    tldr: "Streak-based apps backfire because they reward unbroken logging rather than meaningful action, so users protect the number with trivial entries and abandon the tool entirely once the streak breaks. Tracking outcomes without streak counters keeps a missed day as information instead of a failure.",
    faqs: [
      {
        q: "Why do streaks stop working?",
        a: "A streak measures consistency of logging, not quality of action. Once the number matters more than the work, people log trivial entries to protect it — and quit after the first break, because the counter's value was the counter.",
      },
      {
        q: "What should you track instead of a streak?",
        a: "Track the actual move and its outcome, then read the pattern over weeks. Gaps become useful data about what was happening in the business rather than a mark of failure.",
      },
      {
        q: "Is any gamification useful for founders?",
        a: "Gamification helps for habits with no intrinsic feedback. Running a business already produces feedback — revenue, replies, shipped work — so added points usually compete with the real signal.",
      },
    ],
  },
  "founder-crm-contact-interaction-log": {
    tldr: "A founder CRM is a simple contact list with memory: who the person is, when you last spoke, and what was said. It is not a sales pipeline with stages and forecasts — for a solo founder, the useful data is the interaction log, because it tells you who is going cold.",
    faqs: [
      {
        q: "Do solo founders need a CRM?",
        a: "Solo founders need a contact log more than a CRM. A list of people with the date and substance of the last conversation covers most of the value; pipeline stages and forecasting add overhead without adding decisions.",
      },
      {
        q: "What should you record after a conversation?",
        a: "Record the date, what was actually said, and the next step with a date. Three lines written the same day beat a detailed summary written next week.",
      },
      {
        q: "How often should you follow up with a contact?",
        a: "Follow up on the cadence you promised, and otherwise let the log tell you: any relationship that matters and has gone quiet for a quarter deserves a note.",
      },
    ],
  },
  "what-to-do-when-you-miss-a-week": {
    tldr: "If you miss a week of tracking, do not backfill it. Start again today with one move, and treat the gap as data about what the business demanded that week. Systems fail when people try to reconstruct a perfect record instead of resuming an honest one.",
    faqs: [
      {
        q: "Should you backfill missed days in a tracker?",
        a: "No. Backfilled entries are reconstructions, not records, and they poison the one thing the log is good for — telling you what really happened. Resume today and leave the gap visible.",
      },
      {
        q: "How do you restart a habit after breaking it?",
        a: "Restart at the smallest honest unit: one move, today, for one business. Rebuilding the full routine on day one is the most common way a restart fails a second time.",
      },
      {
        q: "What does a gap in the record tell you?",
        a: "Gaps usually cluster around firefighting, travel or dread. Reading where they cluster is more useful than the entries themselves.",
      },
    ],
  },
  "best-tools-solo-founders-multiple-businesses": {
    tldr: "Solo founders running several businesses need five tool categories: a per-business progress record, a lightweight contact log, separated bookkeeping, one shared calendar, and a single place for ideas. Adding more tools than that usually costs more attention than it returns.",
    faqs: [
      {
        q: "What tools does a solo founder actually need?",
        a: "A record of daily progress per business, a contact log with memory, bookkeeping kept separate per entity, one calendar across everything, and a single ideas inbox. Everything else is optional.",
      },
      {
        q: "Should each business have its own tool stack?",
        a: "No — duplicate stacks multiply maintenance. Use tools that separate data per business inside one account so you switch context without switching software.",
      },
      {
        q: "How do you avoid tool overload?",
        a: "Adopt a tool only when it replaces something, and delete anything you have not opened in a month. Attention, not licence cost, is the scarce resource.",
      },
    ],
  },
  "importance-of-setting-goals": {
    tldr: "Goals matter for founders only when they are specific, written down, and attached to inputs you control. Vague goals like \"grow the business\" feel good and force no decision; a written target with a date and a weekly input turns intention into something you can be wrong about.",
    faqs: [
      {
        q: "Why is goal setting important for founders?",
        a: "A written, specific goal forces a decision about what not to do. Without it, effort spreads across everything that feels urgent, and there is no way to tell a good week from a busy one.",
      },
      {
        q: "What makes a business goal effective?",
        a: "An effective goal names a measurable outcome, a date, and the weekly input you control. The input is the part you actually manage; the outcome is how you check whether the input was the right bet.",
      },
      {
        q: "How often should you review your goals?",
        a: "Review inputs weekly and outcomes monthly. Reviewing outcomes daily produces anxiety, and reviewing them quarterly means you find out too late.",
      },
    ],
  },
  "repetition-decides-who-you-become": {
    tldr: "Repetition physically rewires the brain: circuits used often are strengthened and circuits neglected are pruned, a process called neuroplasticity. For founders this means discipline is less about willpower than about choosing which few actions get repeated daily until they run without negotiation.",
    faqs: [
      {
        q: "How does neuroplasticity relate to discipline?",
        a: "Neuroplasticity means repeated actions strengthen the neural circuits that carry them, so behaviour that once required effort eventually requires less. Discipline is largely the accumulated result of what you repeated.",
      },
      {
        q: "How long does it take to rewire a habit?",
        a: "There is no fixed number of days. What reliably matters is frequency, focused attention during the repetition, and enough difficulty that the action is not automatic yet.",
      },
      {
        q: "Why does struggle matter when learning something new?",
        a: "The effortful, slightly frustrating part of practice is the signal that the brain is being asked to change. Practice that feels easy is mostly rehearsing what you can already do.",
      },
    ],
  },
};
